// Oclusão dos pins BCF: o pin está atrás da geometria que se vê?
//
// Os pins eram projectados do ponto e desenhados por cima de tudo. Com os
// conflitos do Cha139 concentrados no interior, centenas de pins atrás da
// fachada pareciam a flutuar ao lado do edifício. Agora um pin tapado
// fica esbatido (ou escondido, se se pedir) e não tapa os da frente.
//
// Método: um raio pelo pixel do pin contra os modelos visíveis, com o
// raycast dos Fragments (o mesmo da selecção), em lotes e só quando a
// câmara pára. Compara-se a profundidade do primeiro impacto visível com
// a do pin. Escolheu-se o raio e não a leitura do buffer de profundidade
// porque o renderizador do Fragments não expõe um buffer de profundidade
// legível sem um segundo passe de desenho, e o raio já existe, é exacto
// e respeita os elementos escondidos como a selecção os respeita.
//
// Os planos de corte: o raycast dos Fragments não sabe deles (o viewer
// não lhe passa os planos), por isso com corte activo pede-se TODOS os
// impactos do raio e descartam-se os que ficam fora do volume visível
// (`dentroDoCorte`). Sem corte basta o primeiro impacto, que é mais
// barato.
//
// Este ficheiro é a parte pura: as contas e a orquestração, com o
// lançador de raios injectado. A parte que fala com o viewer está em
// pinOclusaoCena.ts.

import { dentroDoCorte, haCorte, type Corte, type Vec3 } from './pinCorte'

/** Tolerância mínima, em metros. A âncora de um conflito está DENTRO dos
 *  elementos que se cruzam (o centro da interferência, ou o centro da
 *  caixa dos elementos): o raio bate primeiro na face do próprio elemento,
 *  uns centímetros a meio metro antes da âncora. Sem esta folga, todos os
 *  pins de conflito ficavam "tapados" pelo elemento que os origina. */
export const TOLERANCIA_MINIMA_M = 1

/** Parte da distância que entra na tolerância: ao longe, a precisão do
 *  raio pelo pixel diminui e um metro deixa de chegar. */
export const TOLERANCIA_RELATIVA = 0.02

/** Espera, em milissegundos, depois de a câmara parar, antes de testar. */
export const ESPERA_DA_CAMARA_MS = 250

/** Quantos raios se lançam em paralelo em cada lote. */
export const RAIOS_POR_LOTE = 16

/** Quantos pins se testam, no máximo, por passagem. Os outros (os mais
 *  afastados) contam como visíveis até a câmara se aproximar. */
export const MAXIMO_DE_PINS_TESTADOS = 600

export interface Camara {
  posicao: Vec3
  /** Direcção do olhar (não precisa de vir normalizada). */
  direcao: Vec3
}

/** Profundidade de um ponto ao longo do olhar. Serve às duas câmaras: na
 *  perspectiva, dois pontos no mesmo raio têm as profundidades na mesma
 *  ordem das distâncias; na ortográfica, os raios são paralelos ao olhar. */
export function profundidade(p: Vec3, c: Camara): number {
  const n = Math.hypot(c.direcao[0], c.direcao[1], c.direcao[2]) || 1
  return (
    ((p[0] - c.posicao[0]) * c.direcao[0] +
      (p[1] - c.posicao[1]) * c.direcao[1] +
      (p[2] - c.posicao[2]) * c.direcao[2]) /
    n
  )
}

export function toleranciaDeOclusao(profundidadeDoPin: number): number {
  return Math.max(TOLERANCIA_MINIMA_M, Math.abs(profundidadeDoPin) * TOLERANCIA_RELATIVA)
}

/** Decide: tapado quando há um impacto visível claramente antes do pin. */
export function pinTapado(profundidadeDoImpacto: number | null, profundidadeDoPin: number): boolean {
  if (profundidadeDoImpacto === null || !Number.isFinite(profundidadeDoImpacto)) return false
  return profundidadeDoImpacto < profundidadeDoPin - toleranciaDeOclusao(profundidadeDoPin)
}

/** A profundidade do primeiro impacto que se vê: à frente da câmara e
 *  dentro do volume do corte. Null sem nenhum. */
export function primeiroImpactoVisivel(impactos: readonly Vec3[], camara: Camara, corte: Corte): number | null {
  let melhor: number | null = null
  for (const p of impactos) {
    const d = profundidade(p, camara)
    if (d <= 0) continue
    if (!dentroDoCorte(p, corte)) continue
    if (melhor === null || d < melhor) melhor = d
  }
  return melhor
}

export interface PedidoDeOclusao {
  chave: string
  ancora: Vec3
  /** Posição do pin no canvas, em pixels. */
  x: number
  y: number
}

/** Lança um raio pelo pixel e devolve os pontos de impacto (no mundo),
 *  todos (`todos`) ou só o primeiro de cada modelo. Null quando não deu
 *  para lançar (o pin conta como visível). */
export type LancarRaio = (pedido: PedidoDeOclusao, todos: boolean) => Promise<Vec3[] | null>

/**
 * Testa os pins em lotes e devolve as chaves dos tapados.
 *
 * - Testa primeiro os mais próximos e no máximo `maximo`.
 * - Pára assim que `cancelado()` disser que sim (a câmara mexeu-se): o
 *   resultado parcial devolve-se, mas quem chama deita-o fora.
 * - Um raio que falha (excepção ou null) deixa o pin como visível: na
 *   dúvida, não se esconde nada.
 */
export async function calcularOclusao(
  pedidos: readonly PedidoDeOclusao[],
  lancar: LancarRaio,
  o: {
    camara: Camara
    corte: Corte
    cancelado?: () => boolean
    lote?: number
    maximo?: number
  },
): Promise<{ tapados: Set<string>; testados: number; completo: boolean }> {
  const lote = Math.max(1, o.lote ?? RAIOS_POR_LOTE)
  const maximo = o.maximo ?? MAXIMO_DE_PINS_TESTADOS
  const todos = haCorte(o.corte)
  const ordenados = pedidos
    .map((p) => ({ p, d: profundidade(p.ancora, o.camara) }))
    .filter((x) => x.d > 0)
    .sort((a, b) => a.d - b.d)
    .slice(0, maximo)
  const tapados = new Set<string>()
  let testados = 0
  for (let i = 0; i < ordenados.length; i += lote) {
    if (o.cancelado?.()) return { tapados, testados, completo: false }
    const fatia = ordenados.slice(i, i + lote)
    const res = await Promise.all(fatia.map((x) => lancar(x.p, todos).catch(() => null)))
    if (o.cancelado?.()) return { tapados, testados, completo: false }
    fatia.forEach((x, j) => {
      testados += 1
      const impactos = res[j]
      if (!impactos) return
      if (pinTapado(primeiroImpactoVisivel(impactos, o.camara, o.corte), x.d)) tapados.add(x.p.chave)
    })
  }
  return { tapados, testados, completo: true }
}
